import {
  Badge,
  Button,
  Card,
  DatePicker,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuditListEntries, useAuditListUsers } from '@/api/generated/endpoints/audit/audit';
import type { AuditEntryOut, AuditEntryOutKind } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { ClientSelect } from '@/components/ClientSelect';
import { ProductSelect } from '@/components/ProductSelect';
import { QtyCell } from '@/components/qty';
import { fieldValue, isChange } from '@/lib/changes';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';
import { CorrectionActions } from '@/pages/audit/CorrectionActions';

const DATETIME = 'DD.MM.YYYY HH:mm';
const API_DATE = 'YYYY-MM-DD';

const AUDIT_KINDS: AuditEntryOutKind[] = [
  'receipt',
  'writeoff',
  'adjustment',
  'shipment_created',
  'shipment_status',
  'upd_upload',
  'upd_resolution',
  'upd_resolve_again',
  'upd_rejected',
  'catalog_change',
  'login',
  'login_failed',
  'correction',
];

/** У этих видов «что» — название из справочника или адрес входа, а не комментарий. */
const NO_COMMENT: AuditEntryOutKind[] = ['catalog_change', 'login', 'login_failed'];

const KIND_COLOR: Record<AuditEntryOutKind, string> = {
  receipt: 'green',
  writeoff: 'red',
  adjustment: 'orange',
  shipment_created: 'default',
  shipment_status: 'blue',
  upd_upload: 'cyan',
  upd_resolution: 'cyan',
  upd_resolve_again: 'cyan',
  upd_rejected: 'volcano',
  catalog_change: 'purple',
  login: 'default',
  login_failed: 'magenta',
  correction: 'gold',
};

const FILTERS = ['actor', 'kind', 'from', 'to', 'product', 'client'] as const;
type Filter = (typeof FILTERS)[number];

const toId = (v: string | undefined) => (v ? Number(v) : null);

/** «Отгрузка: Черновик → Поступило на склад»; остальное — название вида действия. */
function actionText(t: TFunction, e: AuditEntryOut): string {
  if (e.kind === 'shipment_status' && e.status_from && e.status_to) {
    return t('audit.statusChange', {
      from: t(`status.shipment.${e.status_from}.label`),
      to: t(`status.shipment.${e.status_to}.label`),
    });
  }
  if (e.correction) return t(`audit.correctionKind.${e.correction.kind}`);
  if (e.catalog) {
    return t('audit.catalogChange', {
      entity: t(`audit.entity.${e.catalog.entity}`),
      op: t(`audit.op.${e.catalog.op}`),
    });
  }
  return t(`audit.kind.${e.kind}`);
}

function Details({ e }: { e: AuditEntryOut }) {
  const { t } = useTranslation();
  // У исправления «было/стало» — свои поля; служебное описание движения не показываем.
  const raw = e.catalog?.changes ?? e.correction?.changes ?? {};
  const changes = Object.entries(raw).filter(
    (kv): kv is [string, [unknown, unknown]] =>
      isChange(kv[1]) && !(e.correction && kv[0].endsWith('_id')),
  );
  const uom = e.correction ? e.product?.base_uom : null;
  const isUpdate = e.catalog ? e.catalog.op === 'update' : e.correction !== null;
  return (
    <ul style={{ margin: 0 }}>
      {e.stock.map((s, i) => (
        <li key={`s${i}`}>
          {t(`movement.${s.type}`)}: {s.product}, {t('audit.batch', { id: s.batch_id })} —{' '}
          <QtyCell value={s.qty} uom={s.uom} />
        </li>
      ))}
      {changes.map(([field, [before, after]]) => (
        <li key={field}>
          {t(`audit.field.${field}`, { defaultValue: field })}:{' '}
          {isUpdate
            ? `${fieldValue(t, before, field, uom)} → ${fieldValue(t, after, field, uom)}`
            : fieldValue(t, e.catalog?.op === 'delete' ? before : after)}
        </li>
      ))}
    </ul>
  );
}

function Subject({ e }: { e: AuditEntryOut }) {
  const { t } = useTranslation();
  const parts = [];
  if (e.kind === 'catalog_change' && e.comment) parts.push(<strong key="c">{e.comment}</strong>);
  if (e.kind === 'login_failed' && e.comment) {
    parts.push(<span key="l">{t('audit.attemptedLogin', { login: e.comment })}</span>);
  }
  if (e.ip) {
    parts.push(
      <Typography.Text key="ip" type="secondary">
        {t('audit.ip', { ip: e.ip })}
      </Typography.Text>,
    );
  }
  if (e.product && e.kind !== 'catalog_change') {
    parts.push(
      <span key="p">
        {e.product.name}
        {e.batch_id !== null && (
          <Typography.Text type="secondary">
            {' '}
            · {t('audit.batch', { id: e.batch_id })}
          </Typography.Text>
        )}
      </span>,
    );
  }
  if (e.shipment) {
    parts.push(
      <span key="s">
        <Link to={`/shipments/${e.shipment.id}`}>{e.shipment.name}</Link>
        {e.client && <Typography.Text type="secondary"> · {e.client.name}</Typography.Text>}
      </span>,
    );
  }
  if (e.upd) {
    parts.push(
      <Link key="u" to={`/upd/${e.upd.id}`}>
        {t('audit.upd', { label: e.upd.name })}
      </Link>,
    );
  }
  return (
    <Space direction="vertical" size={0}>
      {parts}
    </Space>
  );
}

/** Журнал действий сотрудников (ADR-0012): кто, что и когда сделал. */
export function AuditPage() {
  const { t } = useTranslation();
  const table = useTableState<Filter>({ filters: FILTERS });
  const f = table.filters;
  const users = useAuditListUsers();
  const isAdmin = useHasRole('admin');
  const mobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const list = useAuditListEntries({
    ...table.query,
    actor_id: f.actor.length ? f.actor.map(Number) : null,
    kind: f.kind.length ? (f.kind as AuditEntryOutKind[]) : null,
    date_from: f.from[0] ?? null,
    date_to: f.to[0] ?? null,
    product_id: toId(f.product[0]),
    client_id: toId(f.client[0]),
  });

  const actionCell = (e: AuditEntryOut) => (
    <Space size={4} wrap>
      <Tag color={KIND_COLOR[e.kind]}>{actionText(t, e)}</Tag>
      {e.corrected_by.length > 0 && (
        <Tooltip
          title={e.corrected_by.map((c) => (
            <div key={c.id}>
              {t('audit.correctedBy', {
                who: c.actor,
                at: dayjs(c.at).format(DATETIME),
                reason: c.reason,
              })}
            </div>
          ))}
        >
          <Tag color="gold">{t('audit.corrected')}</Tag>
        </Tooltip>
      )}
    </Space>
  );

  const columns: TableColumnsType<AuditEntryOut> = [
    {
      key: 'at',
      title: t('audit.colAt'),
      dataIndex: 'at',
      width: 140,
      render: (d: string) => dayjs(d).format(DATETIME),
    },
    {
      key: 'actor',
      title: t('audit.colActor'),
      width: 180,
      ellipsis: true,
      render: (_, e) => e.actor?.name ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      key: 'action',
      title: t('audit.colAction'),
      width: 300,
      render: (_, e) => actionCell(e),
    },
    { key: 'subject', title: t('audit.colSubject'), render: (_, e) => <Subject e={e} /> },
    {
      key: 'qty',
      title: t('audit.colQty'),
      align: 'right',
      width: 120,
      render: (_, e) =>
        e.qty === null ? null : <QtyCell value={e.qty} uom={e.product?.base_uom} />,
    },
    {
      key: 'comment',
      title: t('audit.colComment'),
      ellipsis: { showTitle: true },
      render: (_, e) => (NO_COMMENT.includes(e.kind) ? null : e.comment),
    },
  ];

  if (isAdmin) {
    columns.push({
      key: 'fix',
      width: 160,
      render: (_, e) => <CorrectionActions entry={e} />,
    });
  }

  // Телефон (T-177): запись — одной ячейкой; исправление (админ) — там же, под текстом.
  const mobileColumns: TableColumnsType<AuditEntryOut> = [
    {
      key: 'entry',
      title: t('audit.colAction'),
      render: (_, e) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          {actionCell(e)}
          <Subject e={e} />
          {e.qty !== null && <QtyCell value={e.qty} uom={e.product?.base_uom} />}
          {!NO_COMMENT.includes(e.kind) && e.comment && <span>{e.comment}</span>}
          <Typography.Text type="secondary">
            {dayjs(e.at).format(DATETIME)}
            {e.actor ? ` · ${e.actor.name}` : ''}
          </Typography.Text>
          {isAdmin && <CorrectionActions entry={e} />}
        </Space>
      ),
    },
  ];
  const active = FILTERS.filter((k) => k !== 'to' && f[k].length > 0).length;
  const fieldWidth = (n: number) => (mobile ? '100%' : n);

  const range = f.from[0] && f.to[0] ? ([dayjs(f.from[0]), dayjs(f.to[0])] as const) : null;

  return (
    <Card title={t('audit.title')}>
      {mobile && (
        <Button
          block
          style={{ marginBottom: 12 }}
          onClick={() => {
            setFiltersOpen((o) => !o);
          }}
        >
          <Space>
            {t('audit.filters')}
            <Badge count={active} size="small" />
          </Space>
        </Button>
      )}
      <Space
        wrap
        direction={mobile ? 'vertical' : 'horizontal'}
        style={{
          marginBottom: 16,
          ...(mobile ? { width: '100%', display: filtersOpen ? undefined : 'none' } : {}),
        }}
      >
        <Select<number[]>
          mode="multiple"
          allowClear
          aria-label={t('audit.filterActor')}
          placeholder={t('audit.filterActor')}
          style={{ minWidth: 220, width: mobile ? '100%' : undefined }}
          value={f.actor.map(Number)}
          options={(users.data ?? []).map((u) => ({
            value: u.id,
            label: u.is_active ? u.full_name : `${u.full_name} (${t('audit.inactive')})`,
          }))}
          optionFilterProp="label"
          onChange={(v) => {
            table.setFilter('actor', v.map(String));
          }}
        />
        <Select<AuditEntryOutKind[]>
          mode="multiple"
          allowClear
          aria-label={t('audit.filterKind')}
          placeholder={t('audit.filterKind')}
          style={{ minWidth: 220, width: mobile ? '100%' : undefined }}
          value={f.kind as AuditEntryOutKind[]}
          options={AUDIT_KINDS.map((k) => ({ value: k, label: t(`audit.kind.${k}`) }))}
          onChange={(v) => {
            table.setFilter('kind', v);
          }}
        />
        <DatePicker.RangePicker
          aria-label={t('audit.filterPeriod')}
          format="DD.MM.YYYY"
          style={mobile ? { width: '100%' } : {}}
          value={range ? [range[0], range[1]] : null}
          onChange={(v) => {
            table.setFilter('from', v?.[0]?.format(API_DATE) ?? null);
            table.setFilter('to', v?.[1]?.format(API_DATE) ?? null);
          }}
        />
        <div style={{ width: fieldWidth(240) }}>
          <ProductSelect
            ariaLabel={t('audit.filterProduct')}
            placeholder={t('audit.filterProduct')}
            value={toId(f.product[0])}
            onChange={(id) => {
              table.setFilter('product', id === null ? null : String(id));
            }}
          />
        </div>
        <ClientSelect
          placeholder={t('audit.filterClient')}
          style={{ width: fieldWidth(220) }}
          value={toId(f.client[0])}
          onChange={(id) => {
            table.setFilter('client', id === null ? null : String(id));
          }}
        />
        <Button block={mobile} onClick={table.reset}>
          {t('audit.reset')}
        </Button>
      </Space>
      <Table<AuditEntryOut>
        // Колонки с «…» включают фиксированную раскладку: в узком окне (программа для Windows,
        // 900–1300 px) — прокрутка внутри таблицы, а не колонки шириной в букву; колонки «Что» и «Комментарий» не сжимаются до буквы.
        scroll={mobile ? {} : { x: isAdmin ? 1400 : 1240 }}
        rowKey="key"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isPending}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        locale={{ emptyText: t('audit.empty') }}
        expandable={{
          rowExpandable: (e) =>
            e.stock.length > 0 ||
            Object.keys(e.catalog?.changes ?? e.correction?.changes ?? {}).length > 0,
          expandedRowRender: (e) => <Details e={e} />,
        }}
      />
    </Card>
  );
}
